import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import { layers } from '@jira/platform__ui-layers';
import EditorPanelIcon from '@atlaskit/icon/core/status-information';
import EditorWarningIcon from '@atlaskit/icon/core/status-warning';
import Select, { components } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type {
	AutoCompleteConfigType,
	Messages,
	OptionRenderProps,
	OptionRenderType,
	OptionsType,
	ValueType,
} from '../../../types/index.tsx';

export type Props<C, T> = {
	isMultiple: boolean;
	values: ValueType<T>[];
	OptionContent: OptionRenderType<T>;
	autoCompleteConfig: AutoCompleteConfigType<C, T>;
	messages: Messages;
	onChange: (value: ValueType<T>) => void;
};

type CSSProperties = Partial<CSSStyleDeclaration>;
const customStyles = {
	menu: (provided: CSSProperties) => ({
		...provided,
		position: 'absolute',
		zIndex: layers.modal,
	}),
} as const;

export const CollapsibleFieldEdit = <C, T>(props: Props<C, T>): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		isMultiple,
		values,
		onChange: propsOnChange,
		OptionContent,
		autoCompleteConfig,
		messages,
	} = props;

	const { context, useAutoComplete } = autoCompleteConfig;
	const autoComplete = useAutoComplete(values, context);

	const onChange = useCallback(
		(selectedOption: OptionsType<T>) => {
			propsOnChange(selectedOption.rawData);
		},
		[propsOnChange],
	);
	const customComponents = useMemo(
		() => ({
			Option: (optionProps: OptionRenderProps<T>) => (
				<components.Option {...optionProps}>
					<OptionContent {...optionProps} />
				</components.Option>
			),
		}),

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const shouldShowSelect = isMultiple || values.length === 0;
	const shouldShowInformation = !isMultiple && values.length === 1;
	const shouldShowWarning = !isMultiple && values.length > 1;

	// use custom filter on input to prevent default filter flickering issue
	const filterOption = (opt: OptionRenderProps<T>) => {
		const { data } = opt;
		const {
			rawData: { id },
		} = data;

		let display = true;
		// don't display already chosen options
		values.forEach((value: ValueType<T>) => {
			if (value.id === id) {
				display = false;
			}
		});

		return display;
	};

	return (
		<>
			{shouldShowSelect && (
				<Select
					// Setting a value of null causes the field to go back to being empty after a selection is made
					value={null}
					autoFocus
					label={formatMessage(messages.searchPlaceholder)}
					// @ts-expect-error - TS2322 - Type '(selectedOption: OptionsType<T>) => void' is not assignable to type '(value: OptionTypeBase | OptionsType<OptionTypeBase> | null, action: ActionMeta<OptionTypeBase>) => void'.
					onChange={onChange}
					// @ts-expect-error - TS2322 - Type '(opt: OptionRenderProps<T>) => boolean' is not assignable to type '(option: Option, rawInput: string) => boolean'.
					filterOption={filterOption}
					placeholder={formatMessage(messages.searchPlaceholder)}
					// @ts-expect-error - Type '{ Option: (optionProps: OptionRenderProps<T>) => JSX.Element; }' is not assignable to type 'Partial<SelectComponents<unknown, boolean, GroupBase<unknown>>>'.
					components={customComponents}
					styles={customStyles}
					{...autoComplete.selectProps}
				/>
			)}
			{shouldShowInformation && (
				<MessageContainer>
					<EditorPanelIcon spacing="spacious" label="info" />
					<Message>{formatMessage(messages.infoSingleItemMessage)}</Message>
				</MessageContainer>
			)}
			{shouldShowWarning && (
				<MessageContainer>
					<EditorWarningIcon
						spacing="spacious"
						label="warning"
						color={token('color.icon.warning')}
					/>
					<Message>{formatMessage(messages.warningMultipleItemMessage)}</Message>
				</MessageContainer>
			)}
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MessageContainer = styled.div({
	color: token('color.text.subtle'),
	display: 'flex',
	alignItems: 'center',
	font: token('font.body.small'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Message = styled.span({
	marginLeft: token('space.050'),
});
